import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { IconButton } from '~/components/IconButton';
import { useUserRoles } from '~settings/api/queries/userRoles';
import { SelectLabels } from '~settings/components/SelectLabels';
import { SelectUserRole } from '~settings/components/SelectUserRole';

interface ExternalPermissionsProps {
  isEditing?: boolean;
  name: string;
}

export function ExternalPermissions({
  isEditing,
  name,
}: ExternalPermissionsProps) {
  const { t } = useTranslation('settings', { keyPrefix: 'user' });

  const { userRoles } = useUserRoles({ targetUserType: 'EXTERNAL' });

  const { fields, append, remove } = useFieldArray({
    name,
  });

  const canRemove = fields.length > 1;
  const canAddMore = fields.length < (userRoles?.data?.length ?? 0);

  return (
    <ul className="flex flex-col gap-md">
      {fields.map((item, index) => (
        <li key={item.id} className="grid grid-cols-3 items-start gap-md">
          <SelectUserRole
            name={`${name}.${index}.role`}
            targetUserType="EXTERNAL"
          />
          <SelectLabels
            type={isEditing ? 'edit' : 'create'}
            name={`${name}.${index}.labels`}
          />
          {canRemove ? (
            <IconButton
              iconType="trash"
              size="xlarge"
              className="self-end"
              onClick={() => remove(index)}
              aria-label={t('removeRole')}
            />
          ) : null}
        </li>
      ))}
      {canAddMore ? (
        <li className="flex items-center">
          <Button
            onClick={() => {
              append({ role: '', labelGroup: '', labels: [] });
            }}
            className="inline-flex items-center justify-center gap-md p-md no-underline [&_i]:text-body-lg"
            type="button"
            variant="tertiary"
          >
            <Icon type="plus" aria-hidden />
            {t('addRole')}
          </Button>
        </li>
      ) : null}
    </ul>
  );
}

ExternalPermissions.displayName = 'ExternalPermissions';
